/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Copy, UserPlus } from 'lucide-react';
import { type JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type CreatedStaffUser, type SaStaffUser } from '../../lib/api/sa.js';
import { useSaStaffUsers, useSetStaffActive } from '../../lib/queries/sa.js';

import { StaffUserForm } from './staff-user-form.js';

function StaffRow({ user }: { readonly user: SaStaffUser }): JSX.Element {
  const { t } = useTranslation();
  const setActive = useSetStaffActive();
  const toggle = useCallback(() => {
    setActive.mutate({ id: user.id, active: !user.active });
  }, [setActive, user.id, user.active]);

  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <td className="py-3 pr-4">
        <p className="font-medium text-slate-800 dark:text-slate-100">{user.fullName}</p>
        <p className="text-xs text-slate-400">{user.email}</p>
      </td>
      <td className="py-3 pr-4 text-sm text-slate-600 dark:text-slate-300">
        {user.roles.map((r) => t(`sa.staff.role.${r}`)).join(', ')}
      </td>
      <td className="py-3 pr-4">
        <Badge
          label={t(user.active ? 'sa.staff.active' : 'sa.staff.inactive')}
          tone={user.active ? 'green' : 'slate'}
        />
      </td>
      <td className="py-3 pr-4 text-sm text-slate-500">
        {user.lastLoginAt !== null ? user.lastLoginAt.slice(0, 10) : '—'}
      </td>
      <td className="py-3 text-right">
        <Button variant="outline" size="sm" onClick={toggle} disabled={setActive.isPending}>
          {t(user.active ? 'sa.staff.disable' : 'sa.staff.enable')}
        </Button>
      </td>
    </tr>
  );
}

function CreatedPanel({
  created,
  onClose,
}: {
  readonly created: CreatedStaffUser;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const copy = useCallback(() => {
    void navigator.clipboard.writeText(created.tempPassword);
  }, [created.tempPassword]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('sa.staff.created.intro', { email: created.email })}
      </p>
      <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
        <code className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {created.tempPassword}
        </code>
        <Button variant="outline" size="sm" onClick={copy}>
          <Copy className="h-4 w-4" aria-hidden="true" />
          {t('sa.staff.created.copy')}
        </Button>
      </div>
      <p className="text-xs text-amber-600">{t('sa.staff.created.warning')}</p>
      <div className="flex justify-end">
        <Button onClick={onClose}>{t('sa.staff.created.done')}</Button>
      </div>
    </div>
  );
}

const COLS = ['member', 'role', 'status', 'lastLogin', 'actions'] as const;

/** Super Admin → Staff Users. Manage Product Admin accounts (owner-only). */
export function SaStaffUsersPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useSaStaffUsers();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<CreatedStaffUser | null>(null);
  const rows = query.data ?? [];

  const openModal = useCallback(() => {
    setCreated(null);
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
    setCreated(null);
  }, []);

  return (
    <div className="p-8">
      <PageHeader
        title={t('sa.staff.title')}
        subtitle={t('sa.staff.count', { count: rows.length })}
        action={
          <Button onClick={openModal}>
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            {t('sa.staff.add')}
          </Button>
        }
      />
      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={rows.length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('sa.staff.empty')}
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-700">
                {COLS.map((col) => (
                  <th key={col} className="pb-2 pr-4 font-medium">
                    {t(`sa.staff.col.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <StaffRow key={user.id} user={user} />
              ))}
            </tbody>
          </table>
        </QueryState>
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={created === null ? t('sa.staff.add') : t('sa.staff.created.title')}
        closeLabel={t('common.cancel')}
      >
        {created === null ? (
          <StaffUserForm onCreated={setCreated} />
        ) : (
          <CreatedPanel created={created} onClose={closeModal} />
        )}
      </Modal>
    </div>
  );
}
